import type { ReactNode } from 'react';
import type { TaskDef } from '@continuum/show-core';
import { Hero, PrimaryButton, mono } from '../ui/atoms.js';
import type { ScreenCopy } from '../ui/screenText.js';
import { MushroomSprite } from './Forage.js';
import type { Era } from './art.js';
import { thirtyBoard } from './thirty/index.js';
import { DemoPlayer, INK, readable } from './thirty/kit.js';
import type { Lang } from '../ui/text.js';

/**
 * The card before a game (Marek's test evening, 2026-09-28): the move, shown once in a loop, and
 * START — which is when the game and its clock begin. A guest who has just put their glass down
 * gets a moment to see what to do before anything moves or counts.
 *
 * A converted game of the thirty (Marek, 6 Oct 2026: "all games need pre-view animations of how
 * to play") makes its demo the card: the real board, in tonight's era, played by a ghost finger,
 * with the numbered steps under it. The others keep their small SVG loop and one plain line.
 * Every loop is calm on purpose: nothing in them lights up more than about twice a second.
 */
export function HowToCard({
  def,
  accent,
  s,
  lang = 'en',
  era = 'stars',
  demoAt,
  onStart,
}: {
  def: TaskDef;
  accent: string;
  s: ScreenCopy;
  lang?: Lang;
  /** The era the board is drawn in tonight, for a demo of the real board. */
  era?: Era;
  /** A still of the demo, this many ms into its loop (the practice page's `&demoAt=`). */
  demoAt?: number | undefined;
  onStart: () => void;
}) {
  const module = def.type === 'PLAY' ? thirtyBoard(def.game) : undefined;
  if (module?.View && module.demo) {
    return (
      <div className="cc-howto" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
        <style>{SHORT_SCREEN}</style>
        <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.8, color: accent }}>{s.howEyebrow}</div>
        {/* The demo is the card: the board as wide as the screen allows (narrower on a short one, so START stays in reach), the steps under it. */}
        <DemoPlayer
          View={module.View}
          script={module.demo}
          era={era}
          accent={readable(accent)}
          lang={lang}
          at={demoAt}
          boardStyle={{ width: 'min(100%, max(220px, calc(100dvh - 330px)))', margin: '0 auto' }}
        />
        <div className="cc-howto-note" style={{ fontFamily: mono, fontSize: 12, letterSpacing: 0.5, color: INK.dim, textAlign: 'center' }}>
          {s.startNote}
        </div>
        <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
          <PrimaryButton big bg={accent} fg="#05070a" onClick={onStart}>
            {s.startGame}
          </PrimaryButton>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
      <div style={{ alignSelf: 'stretch', fontFamily: mono, fontSize: 11, letterSpacing: 1.8, color: accent, textAlign: 'left' }}>{s.howEyebrow}</div>
      <div
        className="cc-demo"
        style={{
          width: '100%',
          maxWidth: 320,
          borderRadius: 14,
          border: `1px solid ${accent}44`,
          background: '#0a0e14',
          padding: '10px 8px',
          boxSizing: 'border-box',
        }}
      >
        <Demo def={def} accent={accent} s={s} lang={lang} />
      </div>
      <Hero style={{ maxWidth: 340 }}>{s.howMove(def)}</Hero>
      <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 0.5, color: '#8a9aa6' }}>{s.startNote}</div>
      <div style={{ marginTop: 4, width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column' }}>
        <PrimaryButton big bg={accent} fg="#05070a" onClick={onStart}>
          {s.startGame}
        </PrimaryButton>
      </div>
    </div>
  );
}

/**
 * On a short screen (a small Android) the card keeps the board as big as it can: only the step
 * showing now is listed, and the line about the clock goes.
 */
const SHORT_SCREEN = `@media (max-height: 700px) {
  .cc-howto .cc-demo-steps li:not(.on) { display: none !important; }
  .cc-howto .cc-howto-note { display: none; }
}`;

/** A finger's tap: a ring that opens and fades, at each of `at` (fractions of the loop). */
function Tap({ x, y, at, dur, color }: { x: number; y: number; at: readonly number[]; dur: number; color: string }) {
  const keys: number[] = [0];
  const r: number[] = [0];
  const o: number[] = [0];
  for (const t of at) {
    keys.push(t, Math.min(0.999, t + 0.01), Math.min(0.9995, t + 0.14));
    r.push(0, 4, 17);
    o.push(0, 0.9, 0);
  }
  keys.push(1);
  r.push(0);
  o.push(0);
  const kt = keys.map((k) => k.toFixed(4)).join(';');
  return (
    <circle cx={x} cy={y} r="0" fill="none" stroke={color} strokeWidth="2.5" opacity="0">
      <animate attributeName="r" values={r.join(';')} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
      <animate attributeName="opacity" values={o.join(';')} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
    </circle>
  );
}

/** On from `from` to `to` (fractions of the loop), otherwise off. */
function Shown({ from, to, dur, children, base = 0 }: { from: number; to: number; dur: number; children: ReactNode; base?: number }) {
  const kt = [0, from, Math.min(0.999, from + 0.01), to, Math.min(0.9995, to + 0.01), 1].map((k) => k.toFixed(4)).join(';');
  return (
    <g opacity={base}>
      <animate attributeName="opacity" values={`${base};${base};1;1;${base};${base}`} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
      {children}
    </g>
  );
}

function Demo({ def, accent, s, lang }: { def: TaskDef; accent: string; s: ScreenCopy; lang: Lang }) {
  const svg = (children: ReactNode) => (
    <svg viewBox="0 0 220 120" style={{ display: 'block', width: '100%', height: 'auto' }} aria-hidden="true">
      {children}
    </svg>
  );
  switch (def.type) {
    case 'PLAY': {
      const Loop = thirtyBoard(def.game)?.Demo;
      return Loop ? <Loop accent={accent} lang={lang} /> : null;
    }
    case 'THREAD': {
      const dur = 3.2;
      return svg(
        <>
          <g>
            <animateTransform attributeName="transform" type="translate" values="230 0;-40 0" dur={`${dur}s`} repeatCount="indefinite" />
            <rect x="0" y="0" width="14" height="30" rx="3" fill={accent} opacity=".8" />
            <rect x="0" y="80" width="14" height="40" rx="3" fill={accent} opacity=".8" />
          </g>
          <path d="M0 -7 L14 0 L0 7 L4 0 Z" fill="#dfe9f2">
            <animateTransform
              attributeName="transform"
              type="translate"
              values="56 72;56 50;56 64;56 48;56 60;56 72"
              keyTimes="0;0.15;0.4;0.55;0.8;1"
              dur={`${dur}s`}
              repeatCount="indefinite"
            />
          </path>
          <Tap x={190} y={100} at={[0.02, 0.42]} dur={dur} color={accent} />
        </>,
      );
    }
    case 'RHYTHM': {
      const dur = 2.8;
      const pads: [number, number][] = [
        [48, 0],
        [114, 1],
      ];
      return svg(
        <>
          {pads.map(([x, lane]) => (
            <g key={x}>
              <rect x={x} y="4" width="58" height="112" rx="8" fill="rgba(8,12,18,.8)" stroke={`${accent}66`} strokeWidth="1.5" />
              <line x1={x + 5} y1="78" x2={x + 53} y2="78" stroke={accent} strokeWidth="2" />
              <text x={x + 29} y="104" textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="1" fontFamily="'IBM Plex Mono', monospace" fill="#dfe9f2">
                {s.rhythmSide(lane, 2)}
              </text>
            </g>
          ))}
          <circle cx="77" cy="12" r="9" fill={accent}>
            <animate attributeName="cy" values="12;78;78;12" keyTimes="0;0.42;0.43;1" dur={`${dur}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.42;0.43;1" dur={`${dur}s`} repeatCount="indefinite" />
          </circle>
          <circle cx="143" cy="12" r="9" fill={accent} opacity="0">
            <animate attributeName="cy" values="12;12;78;78" keyTimes="0;0.45;0.87;1" dur={`${dur}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.449;0.45;0.87;0.871;1" dur={`${dur}s`} repeatCount="indefinite" />
          </circle>
          <Tap x={77} y={90} at={[0.42]} dur={dur} color="#dfe9f2" />
          <Tap x={143} y={90} at={[0.87]} dur={dur} color="#dfe9f2" />
        </>,
      );
    }
    case 'VINE': {
      const dur = 3;
      return svg(
        <>
          <rect x="16" y="10" width="140" height="100" rx="6" fill="none" stroke="rgba(255,255,255,.12)" />
          <path d="M34 92 H112 V36" fill="none" stroke={accent} strokeWidth="9" strokeLinecap="round" strokeDasharray="140" strokeDashoffset="140">
            <animate attributeName="stroke-dashoffset" values="140;0;0" keyTimes="0;0.7;1" dur={`${dur}s`} repeatCount="indefinite" />
          </path>
          <Shown from={0} to={0.7} dur={dur} base={0}>
            <circle cx="112" cy="36" r="6" fill="#f6e7a8" />
          </Shown>
          <text x="186" y="44" textAnchor="middle" fontSize="18" fill={accent}>▲</text>
          <text x="186" y="88" textAnchor="middle" fontSize="18" fill={accent}>▶</text>
          <Tap x={186} y={82} at={[0.02]} dur={dur} color="#dfe9f2" />
          <Tap x={186} y={38} at={[0.4]} dur={dur} color="#dfe9f2" />
        </>,
      );
    }
    case 'ECHO': {
      const dur = 3.6;
      const pads: [number, number][] = [
        [70, 8],
        [118, 8],
        [70, 62],
        [118, 62],
      ];
      // Watch: pads 0, 3, 1. Then tap them back, the same order.
      const shows: [number, number, number][] = [
        [0, 0.04, 0.16],
        [3, 0.22, 0.34],
        [1, 0.4, 0.52],
        [0, 0.62, 0.68],
        [3, 0.74, 0.8],
        [1, 0.86, 0.92],
      ];
      return svg(
        <>
          {pads.map(([x, y], i) => (
            <rect key={i} x={x} y={y} width="44" height="48" rx="8" fill={`${accent}18`} stroke={`${accent}66`} />
          ))}
          {shows.map(([pad, from, to], i) => (
            <Shown key={i} from={from} to={to} dur={dur}>
              <rect x={pads[pad]![0]} y={pads[pad]![1]} width="44" height="48" rx="8" fill={accent} opacity=".75" />
            </Shown>
          ))}
          <Tap x={92} y={32} at={[0.62]} dur={dur} color="#dfe9f2" />
          <Tap x={140} y={86} at={[0.74]} dur={dur} color="#dfe9f2" />
          <Tap x={140} y={32} at={[0.86]} dur={dur} color="#dfe9f2" />
        </>,
      );
    }
    case 'RECALL': {
      const dur = 3.4;
      const xs = [26, 72, 118, 164];
      return svg(
        <>
          {xs.map((x) => (
            <rect key={x} x={x} y="24" width="34" height="46" rx="6" fill="rgba(255,255,255,.06)" stroke={`${accent}55`} />
          ))}
          {[
            [xs[0]!, 0.12],
            [xs[2]!, 0.42],
          ].map(([x, from]) => (
            <Shown key={x} from={from!} to={0.95} dur={dur}>
              <rect x={x} y="24" width="34" height="46" rx="6" fill={`${accent}30`} stroke={accent} />
              <text x={x! + 17} y="54" textAnchor="middle" fontSize="20" fill={accent}>★</text>
            </Shown>
          ))}
          <Shown from={0.62} to={0.95} dur={dur}>
            <text x="110" y="100" textAnchor="middle" fontSize="15" fontFamily="'IBM Plex Mono', monospace" fill={accent}>✓ PAIR</text>
          </Shown>
          <Tap x={43} y={47} at={[0.12]} dur={dur} color="#dfe9f2" />
          <Tap x={135} y={47} at={[0.42]} dur={dur} color="#dfe9f2" />
        </>,
      );
    }
    case 'DIVIDE': {
      // The bar slides along its track; the tap lands as its light band passes the finger.
      const dur = 3.6;
      const w = 110;
      const cuts = Array.from({ length: def.shares - 1 }, (_, i) => (w * (i + 1)) / def.shares);
      const zone = 13;
      return svg(
        <>
          <rect x="10" y="38" width="200" height="44" rx="8" fill="rgba(255,255,255,.02)" stroke="rgba(255,255,255,.12)" strokeDasharray="4 4" />
          <g>
            <animateTransform
              attributeName="transform"
              type="translate"
              values="10 0;100 0;55 0;55 0"
              keyTimes="0;0.3;0.45;1"
              calcMode="spline"
              keySplines="0.45 0 0.55 1;0.45 0 0.55 1;0 0 1 1"
              dur={`${dur}s`}
              repeatCount="indefinite"
            />
            <rect x="0" y="41" width={w} height="38" rx="6" fill={`${accent}14`} stroke={`${accent}88`} strokeWidth="1.5" />
            {cuts.map((x) => (
              <rect key={`z${x}`} x={x - zone} y="41" width={zone * 2} height="38" fill={`${accent}26`} />
            ))}
            {cuts.map((x, i) => (
              <Shown key={`c${x}`} from={0.45 + i * 0.12} to={0.95} dur={dur}>
                <line x1={x} y1="37" x2={x} y2="83" stroke={accent} strokeWidth="3" />
              </Shown>
            ))}
          </g>
          {cuts.map((x, i) => (
            <Tap key={`t${x}`} x={55 + x} y={60} at={[0.45 + i * 0.12]} dur={dur} color="#dfe9f2" />
          ))}
          <Shown from={0.5 + (cuts.length - 1) * 0.12} to={0.95} dur={dur}>
            <text x="110" y="108" textAnchor="middle" fontSize="15" fontFamily="'IBM Plex Mono', monospace" fill={accent}>✓</text>
          </Shown>
        </>,
      );
    }
    case 'FORAGE': {
      const dur = 3.6;
      return svg(
        <>
          <defs>
            <radialGradient id="howto-candle">
              <stop offset="0" stopColor="rgba(255,196,120,.5)" />
              <stop offset="1" stopColor="rgba(224,138,60,0)" />
            </radialGradient>
          </defs>
          <rect x="8" y="4" width="204" height="112" rx="8" fill="#0a0704" />
          <circle r="34" fill="url(#howto-candle)">
            <animateMotion path="M50 80 Q100 20 150 58 L150 58" keyPoints="0;1;1" keyTimes="0;0.5;1" calcMode="linear" dur={`${dur}s`} repeatCount="indefinite" />
          </circle>
          <g opacity="0">
            <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.35;0.5;0.7;0.72;1" dur={`${dur}s`} repeatCount="indefinite" />
            <svg x="136" y="44" width="30" height="30" viewBox="0 0 40 40">
              <MushroomSprite />
            </svg>
          </g>
          <Tap x={151} y={60} at={[0.7]} dur={dur} color="#ffd9a0" />
          <Shown from={0.74} to={0.95} dur={dur}>
            <text x="110" y="104" textAnchor="middle" fontSize="14" fontFamily="'IBM Plex Mono', monospace" fill="#e0a860">+1</text>
          </Shown>
        </>,
      );
    }
    default:
      return null;
  }
}
